import { ComputedRef, Ref, computed } from 'vue';
import { useMutableQuery } from './useMutableQuery';

import { getAccountInformation } from '@/services/servicesInfo.service';
import { SoldProductType } from '@/entities/ciso.entity';

export type AccountInformation = {
  products: ComputedRef<string[]>;
  hasTechnologyChronicle: ComputedRef<boolean>;
  hasTechnologySentinelOne: ComputedRef<boolean>;
  hasTechnologyCrowdStrike: ComputedRef<boolean>;
  hasProductVulnerability: ComputedRef<boolean>;
  hasProductEmailSecured: ComputedRef<boolean>;
  hasProductEDR: ComputedRef<boolean>;
  hasProductSIEM: ComputedRef<boolean>;
  isLoading: Ref<boolean>;
  isFetching: Ref<boolean>;
  isSuccess: Ref<boolean>;
};

export const useAccountInformation = (): AccountInformation => {
  // Render mode: render token sets access_token before dashboards mount, so /accounts/me
  // is authenticated. CISO depends on this data to derive currentSelectedTenant.soldProducts.
  const [{ data, isLoading, isFetching, isSuccess }] = useMutableQuery({
    queryKey: ['getAccountInformation'],
    queryFn: async () => getAccountInformation(),
    queryOpts: {
      staleTime: Infinity,
    },
  });

  const products = computed(() => {
    if (!data.value) return [];

    return data.value.productTypes;
  });

  const hasTechnologyChronicle = computed(() => {
    return data.value?.technologies?.includes('Google Chronicle');
  });

  const hasTechnologySentinelOne = computed(() => {
    return data.value?.technologies?.includes('SentinelOne');
  });

  const hasTechnologyCrowdStrike = computed(() => {
    const technologies = data.value?.technologies ?? [];
    return technologies.some((technology) => technology.toLowerCase() === 'crowdstrike');
  });

  const hasProductVulnerability = computed(() => {
    return data.value?.productTypes?.includes(SoldProductType.VULNERABILITY_MANAGEMENT);
  });

  const hasProductEmailSecured = computed(() => {
    return data.value?.productTypes?.includes(SoldProductType.EMAIL_SECURITY);
  });

  const hasProductEDR = computed(() => {
    return data.value?.productTypes?.includes(SoldProductType.EDR);
  });

  const hasProductSIEM = computed(() => {
    return data.value?.productTypes?.includes(SoldProductType.SIEM);
  });

  return {
    products,
    hasTechnologyChronicle,
    hasTechnologySentinelOne,
    hasTechnologyCrowdStrike,
    hasProductVulnerability,
    hasProductEmailSecured,
    hasProductEDR,
    hasProductSIEM,
    isLoading,
    isFetching,
    isSuccess,
  };
};
